import { useCallback, useEffect, useRef, useState } from "react";
import { getBookletDocuments } from "../../../services/bookletPrintService";

/**
 * A booklet's documents (GET exams/booklets/<uuid>/documents/), read again when its revision or status changes
 * (they may become stale or superseded), whenever the page reads its check again (back on the tab, "Check again":
 * a draft's previews go stale when the bank changes its variants) and on reload(). A read answering after a newer
 * one is dropped. Returns {documents (null until read), error, loading, reload}.
 */
export default function useBookletDocuments(booklet) {
  const uuid = booklet?.uuid;
  const revision = booklet?.revision;
  const status = booklet?.status;
  const check = booklet?.check;
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const genRef = useRef(0);

  const reload = useCallback(async () => {
    if (!uuid) return;
    const gen = ++genRef.current;
    setLoading(true);
    try {
      const data = await getBookletDocuments(uuid);
      if (gen !== genRef.current) return;
      setDocuments(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      if (gen === genRef.current) setError(err);
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    reload();
    return () => {
      genRef.current += 1;
    };
  }, [reload, revision, status, check]);

  return { documents, error, loading, reload };
}
